import apiClient from './client'
import { useAuthStore } from '@/store/authStore'
import { buildCamerasListParams } from '@/lib/camerasListParams'
import type {
  EventPose,
  ReviewSummary,
  ReviewVerdict,
  LoginRequest,
  LoginResponse,
  LoginTokensResponse,
  RegisterRequest,
  User,
  CamerasSort,
  Camera,
  CameraMapPin,
  CameraStats,
  CameraCreateRequest,
  CameraUpdateRequest,
  CameraGroup,
  CameraGroupCreateRequest,
  Event,
  FullSizeRequest,
  EventFilters,
  Media,
  MediaFilters,
  Notification,
  NotificationSettings,
  AIModel,
  DetectionClass,
  DetectionClassCreate,
  PaginatedResponse,
  NotificationContact,
  NotificationContactCreate,
  NotificationContactUpdate,
  NotificationContactUpdateResponse,
  ContactCameraLink,
  SIMUsage,
  AdminUser,
  SimMismatch,
  SimAudit,
  AdminUserCreateRequest,
  AdminUserUpdateRequest,
  TestCamera,
  TestCameraDetail,
  TestMediaUpload,
  AnnotationProject,
  AnnotationTask,
  AnnotationTaskDetail,
  AnnotationBBox,
  ImportFilters,
  ImportResult,
  ImportPreview,
  CameraExchangeLog,
  G100Log,
  G100AddDeviceRequest, G100AddDeviceResult, G100ConsoleCamera,
  G100ConsoleCommand,
  G100ConsoleInfo,
  G100ConsoleMessage,
  G100ConsoleSettings,
  G100ConsoleSettingsChange,
  G100Firmware, G100FirmwareUpload, G100PackageLoadResult,
  G100ConsoleSendRequest,
  G100AudioCommand,
  G100AudioDeleteRequest,
  G100AudioDownloadRequest,
  G100AudioFile,
  G100AudioOverview,
  G100AudioSpeciesRequest,
  G100AudioPlaybackRequest,
  AudioConfigResponse,
  AudioConfigPatch,
  DetectorConfigsResponse,
  DetectorConfigPatch,
  GlobalThresholdsResponse,
  GlobalThresholdPatch,
  PushPreferences,
  PushCatalog,
  PushFilters,
  CameraMyPush,
  MyPushRequest,
  MyPushCameraRows,
  GroupMyPushSummary,
  GroupMyPushResult,
  LevelZoneChange,
  LevelZones,
  BulkSettings,
  BulkSettingsPreview,
  BulkSettingsApplied,
  GroupSettingsStatus,
  DefaultEventCategory,
  GroupDefaultEventsViewResult,
  GroupDetectionModelList,
  GroupDetectionModelResult,
  CaptchaConfig,
  GroupAudioClassList,
  GroupAudioClassChange,
  GroupAudioClassResult,
  GroupCriticalSettings,
  GroupCriticalSettingsChange,
  GroupCriticalSettingsResult,
  AcknowledgeAllResult,
  MapSettings,
  CriticalSoundSettings,
  G100LiveConnection,
  G100LiveSession,
  G100LiveStart,
  G100LiveStartRequest,
  G100LiveStatus,
  SitePlan,
  SitePlanPin,
  SitePlanSummary, G100OwnerSettingsChange, G100OwnerStatus,
  G100OwnerAction, G100OwnerActionRequest,
} from '@/types'

// ============================================================================
// Authentication API
// ============================================================================

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post('/auth/login', data)
    return response.data
  },

  register: async (data: RegisterRequest): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/register', data)
    return response.data
  },

  logout: async (fcmToken?: string): Promise<void> => {
    await apiClient.post('/auth/logout', { fcm_token: fcmToken })
  },

  // #235: end this device's session server-side (the refresh token is blacklisted). The bearer is
  // passed explicitly because the caller clears the store straight after, before the request
  // interceptor would read it. Short timeout: logout never waits on this.
  endSession: async (refresh: string, access: string | null): Promise<void> => {
    await apiClient.post(
      '/auth/logout',
      { refresh },
      { timeout: 5000, headers: access ? { Authorization: `Bearer ${access}` } : {} },
    )
  },

  refreshToken: async (refreshToken: string): Promise<{ access: string; refresh: string }> => {
    const response = await apiClient.post('/auth/refresh-token', { refresh: refreshToken })
    return response.data
  },

  verifyEmail: async (email: string, otp: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/verify-email', { email, otp })
    return response.data
  },

  resendOtp: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/resend-otp', { email })
    return response.data
  },

  // #240: the token is sent only when there is one — the server decides whether it is required.
  requestPasswordReset: async (email: string, recaptchaToken?: string): Promise<{ message: string }> => {
    const response = await apiClient.post(
      '/auth/password-reset/request',
      recaptchaToken ? { email, recaptcha_token: recaptchaToken } : { email },
    )
    return response.data
  },

  // #240: public — the reCAPTCHA mode and the public site key (null when off).
  captchaConfig: async (): Promise<CaptchaConfig> => {
    const response = await apiClient.get('/auth/captcha-config', { timeout: 5000 })
    return response.data
  },

  confirmPasswordReset: async (
    email: string,
    otp: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/password-reset/confirm', {
      email,
      otp,
      new_password: newPassword,
      confirm_password: confirmPassword,
    })
    return response.data
  },
}

// ============================================================================
// Two-factor (superuser SMS 2FA) API
// ============================================================================

export const twoFactorApi = {
  verifyLoginOtp: async (challenge_token: string, code: string): Promise<LoginTokensResponse> => {
    const response = await apiClient.post('/auth/login/verify-otp', {
      challenge_token, code, device_type: 'web',
    })
    return response.data
  },
  resendLoginOtp: async (challenge_token: string, channel: 'sms' | 'email'): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/login/resend-otp', { challenge_token, channel })
    return response.data
  },
  phoneEnrollStart: async (phone_number: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/phone/start', { phone_number })
    return response.data
  },
  phoneEnrollVerify: async (code: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/phone/verify', { code })
    return response.data
  },
  phoneDisable: async (password: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/phone/disable', { password })
    return response.data
  },
}

// ============================================================================
// User API
// ============================================================================

export const userApi = {
  getProfile: async (): Promise<User> => {
    const response = await apiClient.get('/user/profile')
    return response.data
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await apiClient.put('/user/profile', data)
    return response.data
  },

  /** #277 — change one or two profile fields (e.g. `cameras_sort`) without re-sending the whole profile. */
  patchProfile: async (data: Partial<User>): Promise<User> => {
    const response = await apiClient.patch('/user/profile', data)
    return response.data
  },

  deleteAccount: async (password: string): Promise<void> => {
    await apiClient.delete('/user/delete', { data: { password } })
  },
}

// ============================================================================
// Cameras API
// ============================================================================

export const camerasApi = {
  getAll: async (search?: string): Promise<Camera[]> => {
    const params: Record<string, string | number> = { page_size: 1000 }
    if (search) params.search = search
    const response = await apiClient.get('/cameras/', { params })
    // Handle both paginated and non-paginated responses
    return response.data.results ?? response.data
  },

  // Superuser-only: every camera across all owners (#095). The backend ignores
  // scope=all for non-superusers, so this is safe to call only from superuser-gated UI.
  getAllAdmin: async (userId?: number, search?: string): Promise<Camera[]> => {
    const params: Record<string, string | number> = { scope: 'all', page_size: 1000 }
    if (userId) params.user_id = userId
    if (search) params.search = search
    const response = await apiClient.get('/cameras/', { params })
    return response.data.results ?? response.data
  },

  getPaginated: async (
    page = 1,
    search?: string
  ): Promise<PaginatedResponse<Camera>> => {
    const params: Record<string, string | number> = { page }
    if (search) params.search = search
    const response = await apiClient.get('/cameras/', { params })
    return response.data
  },

  getInfinite: async (
    page = 1,
    search?: string,
    group?: string,
    ingestionType?: 'http' | 'ftp',
    onlineStatus?: 'online' | 'offline' | 'unknown',
    batteryLow?: boolean,
    ordering?: CamerasSort,  // #277
  ): Promise<PaginatedResponse<Camera>> => {
    const params = buildCamerasListParams({ page, search, group, ingestionType, onlineStatus, batteryLow, ordering })
    const response = await apiClient.get('/cameras/', { params })
    return response.data
  },

  getById: async (id: number): Promise<Camera> => {
    const response = await apiClient.get(`/cameras/${id}/`)
    return response.data
  },

  create: async (data: CameraCreateRequest): Promise<Camera> => {
    const response = await apiClient.post('/cameras/', data)
    return response.data
  },

  update: async (id: number, data: CameraUpdateRequest): Promise<Camera> => {
    const response = await apiClient.patch(`/cameras/${id}/`, data)
    return response.data
  },

  // #373 — a camera delete carries the master password in the body; the API refuses without it.
  delete: async (id: number, masterPassword?: string): Promise<void> => {
    await apiClient.delete(`/cameras/${id}/`, { data: { master_password: masterPassword ?? '' } })
  },

  getMapPins: async (): Promise<CameraMapPin[]> => {
    const response = await apiClient.get('/cameras/map-pins/')
    return response.data
  },

  // Fleet-wide counts for the dashboard stat cards (#121) — counts the whole
  // scope server-side rather than a single paginated page.
  getStats: async (): Promise<CameraStats> => {
    const response = await apiClient.get('/cameras/stats/')
    return response.data
  },

  getSimUsage: async (): Promise<{ usage: Record<string, SIMUsage>; cached: boolean; count: number }> => {
    const response = await apiClient.get('/cameras/sim-usage/')
    return response.data
  },

  getSimCardStatus: async (id: number): Promise<{ card_info: Record<string, unknown>; iccid: string }> => {
    const response = await apiClient.get(`/cameras/${id}/sim-card-status/`)
    return response.data
  },

  // CP 189 — superuser-only; 403 for anyone else, 400 for an FTP camera.
  setOnlineStatusMode: async (
    id: number,
    mode: 'auto' | 'manual',
  ): Promise<{ online_status_mode: 'auto' | 'manual'; is_online: boolean | null }> => {
    const response = await apiClient.patch(`/cameras/${id}/online-status-mode/`, {
      online_status_mode: mode,
    })
    return response.data
  },

  getStatus: async (id: number): Promise<{ is_online: boolean | null; last_seen: string | null }> => {
    const response = await apiClient.get(`/cameras/${id}/status/`)
    return response.data
  },

  getSharedUsers: async (id: number): Promise<User[]> => {
    const response = await apiClient.get(`/cameras/${id}/shared-users/`)
    return response.data
  },

  shareCamera: async (id: number, email: string): Promise<{ message: string }> => {
    const response = await apiClient.post(`/cameras/${id}/share-camera/`, { email })
    return response.data
  },

  unshareCamera: async (id: number, userId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/cameras/${id}/unshare-camera/`, { user_id: userId })
    return response.data
  },

  getAvailableClasses: async (id: number): Promise<{ camera_id: number; available_class_names: string[] }> => {
    const response = await apiClient.get(`/cameras/${id}/available-classes/`)
    return response.data
  },

  getPushCatalog: async (id: number): Promise<PushCatalog> => {
    const response = await apiClient.get(`/cameras/${id}/push-catalog/`)
    return response.data
  },

  updatePushSettings: async (
    id: number,
    data: { push_enabled?: boolean; push_class_names?: string[]; push_filters?: PushFilters },
  ): Promise<{ id: number; push_enabled: boolean; push_class_names: string[]; push_filters: PushFilters }> => {
    const response = await apiClient.patch(`/cameras/${id}/push-settings/`, data)
    return response.data
  },

  // CP 391 — the requester's OWN phone alerts for this camera ("My phone alerts"). Never the camera-wide push
  // settings above: those apply to everyone (CP 159 / 164).
  getMyPush: async (id: number, signal?: AbortSignal): Promise<CameraMyPush> => {
    const response = await apiClient.get(`/cameras/${id}/my-push/`, { signal })
    return response.data
  },

  setMyPush: async (id: number, body: MyPushRequest): Promise<Partial<CameraMyPush>> => {
    const response = await apiClient.put(`/cameras/${id}/my-push/`, body)
    return response.data
  },

  // #406 — the camera's "At the train" zone (superusers): 3–8 points normalised 0–1 over the picture, or null to
  // clear it. Answers the camera's level_zones as stored.
  setLevelZones: async (id: number, body: LevelZoneChange): Promise<{ level_zones: LevelZones }> => {
    const response = await apiClient.put(`/cameras/${id}/level-zones/`, body)
    return response.data
  },

  // #408 — the camera's Beta switch (superusers): the appearance clues run on this camera only while it is on.
  setCluesBeta: async (id: number, enabled: boolean): Promise<{ id: number; clues_beta_enabled: boolean }> => {
    const response = await apiClient.put(`/cameras/${id}/clues-beta/`, { enabled })
    return response.data
  },

  getAudioClasses: async (id: number): Promise<AudioConfigResponse> => {
    const response = await apiClient.get(`/cameras/${id}/audio-classes/`)
    return response.data
  },

  updateAudioClassConfig: async (id: number, patch: AudioConfigPatch): Promise<AudioConfigResponse> => {
    const response = await apiClient.patch(`/cameras/${id}/audio-classes/`, patch)
    return response.data
  },

  getAiModelConfigs: async (id: number): Promise<DetectorConfigsResponse> => {
    const response = await apiClient.get(`/cameras/${id}/ai-model-configs/`)
    return response.data
  },

  updateAiModelConfigs: async (id: number, patch: DetectorConfigPatch): Promise<DetectorConfigsResponse> => {
    const response = await apiClient.patch(`/cameras/${id}/ai-model-configs/`, patch)
    return response.data
  },

  resetAiModelConfigs: async (id: number): Promise<{ cleared: number }> => {
    const response = await apiClient.post(`/cameras/${id}/ai-model-configs/reset/`)
    return response.data
  },
}

// ============================================================================
// Camera Groups API
// ============================================================================

export const cameraGroupsApi = {
  getAll: async (): Promise<CameraGroup[]> => {
    // Groups feed the Groups page plus several dropdowns (filter, bulk-assign,
    // camera edit sheet) — all sharing this query. Follow pagination so a large
    // group list is never truncated to the first page (#103): pre-fix a user
    // with 20+ groups never saw newly created (empty) groups, which sort last.
    // page_size=1000 means one request for any realistic account; the loop only
    // continues past that.
    const all: CameraGroup[] = []
    let page = 1
    for (;;) {
      const { data } = await apiClient.get('/camera-groups/', {
        params: { page, page_size: 1000 },
      })
      if (Array.isArray(data)) return data // non-paginated fallback
      all.push(...(data.results ?? []))
      if (!data.next) break
      page += 1
    }
    return all
  },

  getById: async (id: number): Promise<CameraGroup> => {
    const response = await apiClient.get(`/camera-groups/${id}/`)
    return response.data
  },

  create: async (data: CameraGroupCreateRequest): Promise<CameraGroup> => {
    const response = await apiClient.post('/camera-groups/', data)
    return response.data
  },

  update: async (id: number, name: string): Promise<CameraGroup> => {
    const response = await apiClient.put(`/camera-groups/${id}/`, { name })
    return response.data
  },

  // #373 — a group delete carries the master password in the body; the API refuses without it.
  delete: async (id: number, masterPassword?: string): Promise<void> => {
    await apiClient.delete(`/camera-groups/${id}/`, { data: { master_password: masterPassword ?? '' } })
  },

  // Item 226: bulk settings — preview first (dry_run), then apply with its token.
  previewSettings: async (id: number, settings: BulkSettings, exclude: number[], signal?: AbortSignal): Promise<BulkSettingsPreview> => {
    const { data } = await apiClient.post(`/camera-groups/${id}/apply-settings/`,
      { settings, exclude_camera_ids: exclude, dry_run: true }, { signal })
    return data
  },
  applySettings: async (id: number, settings: BulkSettings, exclude: number[], token: string): Promise<BulkSettingsApplied> => {
    const { data } = await apiClient.post(`/camera-groups/${id}/apply-settings/`,
      { settings, exclude_camera_ids: exclude, dry_run: false, preview_token: token }, { timeout: 60000 })
    return data
  },
  // Item 238: the default events view for every camera in a group the user may change.
  setDefaultEventsView: async (id: number, value: DefaultEventCategory, dryRun: boolean, signal?: AbortSignal): Promise<GroupDefaultEventsViewResult> => {
    const { data } = await apiClient.post(`/camera-groups/${id}/default-events-view/`,
      { default_event_category: value, dry_run: dryRun }, { signal })
    return data
  },
  // CP 391: the requester's own phone alerts for every member camera they are a user of. Anyone who can see the
  // group (not the owner-only gate): it writes only the requester's rows.
  getMyPush: async (id: number, signal?: AbortSignal): Promise<GroupMyPushSummary> => {
    const { data } = await apiClient.get(`/camera-groups/${id}/my-push/`, { signal })
    return data
  },
  setMyPush: async (id: number, body: MyPushRequest, dryRun: boolean, signal?: AbortSignal): Promise<GroupMyPushResult> => {
    const { data } = await apiClient.post(`/camera-groups/${id}/my-push/`, { ...body, dry_run: dryRun }, { signal })
    return data
  },
  // Item 266: an add-on detection model (Train Detection) for every camera in a group the user may change.
  getDetectionModels: async (id: number, signal?: AbortSignal): Promise<GroupDetectionModelList> => {
    const { data } = await apiClient.get(`/camera-groups/${id}/detection-models/`, { signal })
    return data
  },
  setDetectionModel: async (id: number, aiModelId: number, subscribed: boolean, dryRun: boolean, signal?: AbortSignal): Promise<GroupDetectionModelResult> => {
    const { data } = await apiClient.post(`/camera-groups/${id}/detection-models/`,
      { ai_model_id: aiModelId, subscribed, dry_run: dryRun }, { signal })
    return data
  },
  // Item 280: a sound-detection class (and its threshold) for every camera in a group — superusers.
  getAudioClasses: async (id: number, signal?: AbortSignal): Promise<GroupAudioClassList> => {
    const { data } = await apiClient.get(`/camera-groups/${id}/audio-classes/`, { signal })
    return data
  },
  setAudioClass: async (id: number, change: GroupAudioClassChange, dryRun: boolean, signal?: AbortSignal): Promise<GroupAudioClassResult> => {
    const { data } = await apiClient.post(`/camera-groups/${id}/audio-classes/`,
      { ...change, dry_run: dryRun }, { signal })
    return data
  },
  // Item 294: the group's critical alerts (on/off + spray-sound threshold) — superusers. A dry run
  // answers how many cameras would get the Graffiti sound switched on; nothing is written.
  // #406 — the body and the answer also carry the group's level rules (`level_rules`); a dry run with candidate
  // rules answers their 30-day replay too (`preview`).
  getCriticalSettings: async (id: number, signal?: AbortSignal): Promise<GroupCriticalSettings> => {
    const { data } = await apiClient.get(`/camera-groups/${id}/critical/`, { signal })
    return data
  },
  setCriticalSettings: async (id: number, change: GroupCriticalSettingsChange, dryRun: boolean, signal?: AbortSignal): Promise<GroupCriticalSettingsResult> => {
    const { data } = await apiClient.put(`/camera-groups/${id}/critical/`,
      { ...change, dry_run: dryRun }, { signal })
    return data
  },
  getSettingsStatus: async (id: number): Promise<GroupSettingsStatus> => {
    const { data } = await apiClient.get(`/camera-groups/${id}/settings-status/`)
    return data
  },

  addCameras: async (id: number, cameraIds: number[]): Promise<CameraGroup> => {
    const response = await apiClient.post(`/camera-groups/${id}/add-cameras/`, {
      camera_ids: cameraIds,
    })
    return response.data
  },

  removeCameras: async (id: number, cameraIds: number[]): Promise<CameraGroup> => {
    const response = await apiClient.post(`/camera-groups/${id}/remove-cameras/`, {
      camera_ids: cameraIds,
    })
    return response.data
  },

  // Group sharing (#096) — share a whole group by email; recipient gets
  // view access to the group's cameras (+ push; item 410 ticks can add delete / settings); email/SMS only through
  // contacts the recipient links to a group camera (CP 312).
  shareGroup: async (
    id: number,
    email: string,
    ticks?: { can_delete_events?: boolean; can_change_settings?: boolean },
  ): Promise<{ message: string }> => {
    const response = await apiClient.post(`/camera-groups/${id}/share/`, { email, ...ticks })
    return response.data
  },

  // #410 — change one or both ticks of a person the group is shared with; send only the flag that changed.
  updateGroupSharePermissions: async (
    id: number,
    body: { user_id: number; can_delete_events?: boolean; can_change_settings?: boolean },
  ): Promise<{ user_id: number; can_delete_events: boolean; can_change_settings: boolean }> => {
    const response = await apiClient.patch(`/camera-groups/${id}/share-permissions/`, body)
    return response.data
  },

  unshareGroup: async (id: number, userId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/camera-groups/${id}/unshare/`, { user_id: userId })
    return response.data
  },

  getGroupSharedUsers: async (
    id: number,
  ): Promise<{ owner: { id: number; username: string; email: string } | null; shared_users: User[]; total_users: number }> => {
    const response = await apiClient.get(`/camera-groups/${id}/shared-users/`)
    return response.data
  },
}

// ============================================================================
// Site plans API (#363) — a group's site drawing and its hand-placed camera pins
// ============================================================================

export const sitePlansApi = {
  // The groups the user can see that have a drawing: the map window shows its Google map / Site map switch from it.
  list: async (): Promise<SitePlanSummary[]> => {
    const { data } = await apiClient.get('/site-plans/')
    return data
  },

  // A group's drawing (presigned link, 1 hour) and pins. The API answers 404 for a group without a drawing (and for
  // a group the user cannot see): that is "no site map", null here; any other failure throws.
  get: async (groupId: number, signal?: AbortSignal): Promise<SitePlan | null> => {
    try {
      const { data } = await apiClient.get(`/camera-groups/${groupId}/site-plan/`, { signal })
      return data
    } catch (error) {
      if ((error as { response?: { status?: number } } | null)?.response?.status === 404) return null
      throw error
    }
  },

  // Superusers: create or replace the drawing (PNG or JPEG); the pins keep their fractions. The client's default JSON
  // content type would make axios turn the form into JSON; with this one the browser adds the multipart boundary.
  upload: async (groupId: number, image: File, membersCanArrange?: boolean): Promise<void> => {
    const form = new FormData()
    form.append('image', image)
    if (membersCanArrange !== undefined) form.append('members_can_arrange', membersCanArrange ? 'true' : 'false')
    await apiClient.put(`/camera-groups/${groupId}/site-plan/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  // Superusers: only the "group members can arrange pins" switch.
  patch: async (groupId: number, change: { members_can_arrange: boolean }): Promise<void> => {
    await apiClient.patch(`/camera-groups/${groupId}/site-plan/`, change)
  },

  // Superusers: the drawing and all its pins.
  remove: async (groupId: number): Promise<void> => {
    await apiClient.delete(`/camera-groups/${groupId}/site-plan/`)
  },

  // Users who may arrange: the whole set of pins in one PUT (a camera left out loses its pin). Answers the drawing.
  savePins: async (groupId: number, pins: SitePlanPin[]): Promise<SitePlan> => {
    const { data } = await apiClient.put(`/camera-groups/${groupId}/site-plan/pins/`, { pins })
    return data
  },
}

// ============================================================================
// Events API
// ============================================================================

export const eventsApi = {
  getAll: async (
    filters?: EventFilters,
    page = 1
  ): Promise<PaginatedResponse<Event>> => {
    const response = await apiClient.get('/events/', { params: { ...filters, page } })
    return response.data
  },

  getById: async (id: number): Promise<Event> => {
    const response = await apiClient.get(`/events/${id}/`)
    return response.data
  },

  // 409 — pose review (superusers only)
  getEventPose: async (id: number): Promise<EventPose> => {
    const response = await apiClient.get(`/events/${id}/pose/`)
    return response.data
  },

  putEventReview: async (id: number, verdict: ReviewVerdict): Promise<EventPose> => {
    const response = await apiClient.put(`/events/${id}/review/`, { clue: 'hand_at_train', verdict })
    return response.data
  },

  getReviewSummary: async (clue = 'hand_at_train'): Promise<ReviewSummary> => {
    const response = await apiClient.get('/events/review-summary/', { params: { clue } })
    return response.data
  },

  requestFullSize: async (id: number): Promise<FullSizeRequest> => {
    const response = await apiClient.post(`/events/${id}/request-full-size/`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/events/${id}/`)
  },

  // Bulk-delete events in one request (#097). Owner-only per id, max 500.
  // Returns a partial result so one bad id never fails the whole batch.
  bulkDelete: async (
    ids: number[],
  ): Promise<{ deleted: number[]; failed: Array<{ id: number; reason: string }>; s3_failed: number }> => {
    const response = await apiClient.post('/events/bulk-delete/', { ids })
    return response.data
  },

  // #292 — the current user has opened this event (per person). Idempotent.
  markAsRead: async (id: number): Promise<Event> => {
    const response = await apiClient.post(`/events/${id}/read/`)
    return response.data
  },

  // #292 — the current user's events up to `upTo` (the as_of of the last unread count; omitted =
  // now) stop counting as unread. Returns the remaining unread count.
  markAllAsRead: async (upTo?: string): Promise<{ count: number }> => {
    const response = await apiClient.post('/events/read-all/', upTo ? { up_to: upTo } : {})
    return response.data
  },

  // #292 — events the current user has not opened (the dashboard's Recent Events card), with the
  // server time of the count (`as_of`), which "mark all" sends back.
  getUnreadCount: async (): Promise<{ count: number; as_of?: string }> => {
    const response = await apiClient.get('/events/unread-count/')
    return response.data
  },

  // #122 — toggle this event in the current user's favourites. Idempotent.
  setFavorite: async (id: number, is_favorite: boolean): Promise<Event> => {
    const response = await apiClient.post(`/events/${id}/favorite/`, { is_favorite })
    return response.data
  },

  // #294 — acknowledge a critical event (one acknowledgement for the team). Idempotent: a second call
  // keeps the first one's who and when. Returns the event.
  acknowledge: async (id: number): Promise<Event> => {
    const response = await apiClient.post(`/events/${id}/acknowledge/`)
    return response.data
  },

  // #294 — acknowledge these critical events (1–200 ids; the server skips ids the user cannot see or
  // that are already acknowledged). Returns the ids acknowledged now.
  acknowledgeAll: async (eventIds: number[]): Promise<AcknowledgeAllResult> => {
    const response = await apiClient.post('/events/acknowledge-all/', { event_ids: eventIds })
    return response.data
  },

  // #294 redesign — acknowledge every open critical event the user can see created at or before `upTo`:
  // the created_at of the newest loaded open alert, exactly as the API wrote it (never re-formatted, never
  // the PC clock), so one that arrives during the click stays open. An old API answers 400.
  acknowledgeAllUpTo: async (upTo: string): Promise<AcknowledgeAllResult> => {
    const response = await apiClient.post('/events/acknowledge-all/', { up_to: upTo })
    return response.data
  },

  // #406 — mark a suspicious event seen (one Seen for the team, quiet: no alarm to stop). Idempotent: a second
  // call keeps the first one's who and when. `reason` is optional (worker / unsure / real). Returns the event.
  markSeen: async (id: number, reason?: 'worker' | 'unsure' | 'real'): Promise<Event> => {
    const response = await apiClient.post(`/events/${id}/seen/`, reason ? { reason } : {})
    return response.data
  },
}

// ============================================================================
// Media API
// ============================================================================

export const mediaApi = {
  getAll: async (
    filters?: MediaFilters,
    page = 1
  ): Promise<PaginatedResponse<Media>> => {
    const response = await apiClient.get('/media/', { params: { ...filters, page } })
    return response.data
  },

  getById: async (id: number): Promise<Media> => {
    const response = await apiClient.get(`/media/${id}/`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/media/${id}/`)
  },

  search: async (
    filters: MediaFilters,
    page = 1
  ): Promise<PaginatedResponse<Media>> => {
    const response = await apiClient.get('/media/search/', { params: { ...filters, page } })
    return response.data
  },
}

// ============================================================================
// Notifications API
// ============================================================================

export const notificationsApi = {
  getAll: async (page = 1): Promise<PaginatedResponse<Notification>> => {
    const response = await apiClient.get('/notifications/', { params: { page } })
    return response.data
  },

  getUnreadCount: async (): Promise<{ count: number }> => {
    const response = await apiClient.get('/notifications/unread/')
    return response.data
  },

  markAsRead: async (id: number): Promise<Notification> => {
    const response = await apiClient.post(`/notifications/${id}/read/`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/notifications/${id}/`)
  },

  getSettings: async (): Promise<NotificationSettings> => {
    const response = await apiClient.get('/notifications/settings/')
    return response.data
  },

  updateSettings: async (settings: Partial<NotificationSettings>): Promise<NotificationSettings> => {
    const response = await apiClient.post('/notifications/settings/', settings)
    return response.data
  },
}

// ============================================================================
// Push Preferences API (#138)
// ============================================================================
export const pushPreferencesApi = {
  get: async (): Promise<PushPreferences> => {
    const response = await apiClient.get('/notifications/push-preferences/')
    return response.data
  },

  // Whole-map replace, not a merge — send every category.
  update: async (prefs: PushPreferences): Promise<PushPreferences> => {
    const response = await apiClient.put('/notifications/push-preferences/', prefs)
    return response.data
  },

  // The pickable options, grouped by the same rule the push filter uses.
  catalog: async (): Promise<PushCatalog> => {
    const response = await apiClient.get('/notifications/push-preferences/catalog/')
    return response.data
  },

  // CP 391 — the person's own camera rows ("My phone alerts") with the stored filters exactly as the handler reads
  // them, so the in-app bell applies the same filter the phone gets.
  getCameras: async (): Promise<MyPushCameraRows> => {
    const response = await apiClient.get('/notifications/push-preferences/cameras/')
    return response.data
  },
}

// ============================================================================
// AI Models API
// ============================================================================

export const aiModelsApi = {
  getAll: async (): Promise<AIModel[]> => {
    const response = await apiClient.get('/ai-models/')
    // Handle both paginated and non-paginated responses
    return response.data.results ?? response.data
  },

  getById: async (id: number): Promise<AIModel> => {
    const response = await apiClient.get(`/ai-models/${id}/`)
    return response.data
  },

  getDetectionClasses: async (baseModelId?: number): Promise<DetectionClass[]> => {
    const params = baseModelId ? { base_model: baseModelId } : {}
    const response = await apiClient.get('/detection-classes/', { params })
    // Handle both paginated and non-paginated responses
    return response.data.results ?? response.data
  },

  // #108 — superuser species-catalog management (open-vocabulary model only)
  createDetectionClass: async (payload: DetectionClassCreate): Promise<DetectionClass> => {
    const response = await apiClient.post('/detection-classes/', payload)
    return response.data
  },

  deleteDetectionClass: async (id: number): Promise<void> => {
    await apiClient.delete(`/detection-classes/${id}/`)
  },

  getGlobalThresholds: async (): Promise<GlobalThresholdsResponse> => {
    const response = await apiClient.get('/ai-models/global-thresholds/')
    return response.data
  },

  updateGlobalThreshold: async (patch: GlobalThresholdPatch): Promise<GlobalThresholdsResponse> => {
    const response = await apiClient.patch('/ai-models/global-thresholds/', patch)
    return response.data
  },

  resetCameraThresholds: async (): Promise<{ cleared: number }> => {
    const response = await apiClient.post('/ai-models/reset-camera-thresholds/')
    return response.data
  },
}

// ============================================================================
// Notification Contacts API
// ============================================================================

export const notificationContactsApi = {
  getAll: async (): Promise<NotificationContact[]> => {
    const response = await apiClient.get('/notification-contacts/')
    return response.data.results ?? response.data
  },

  create: async (data: NotificationContactCreate): Promise<NotificationContact> => {
    const response = await apiClient.post('/notification-contacts/', data)
    return response.data
  },

  // Item 315 — the PATCH answers only the Update serializer's fields (not a full contact): refetch the contacts.
  update: async (id: number, data: NotificationContactUpdate): Promise<NotificationContactUpdateResponse> => {
    const response = await apiClient.patch(`/notification-contacts/${id}/`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/notification-contacts/${id}/`)
  },

  verify: async (id: number, code: string): Promise<{ detail: string }> => {
    const response = await apiClient.post(`/notification-contacts/${id}/verify/`, { code })
    return response.data
  },

  resendOtp: async (id: number): Promise<{ detail: string }> => {
    const response = await apiClient.post(`/notification-contacts/${id}/resend-otp/`)
    return response.data
  },

  getCameras: async (id: number): Promise<ContactCameraLink[]> => {
    const response = await apiClient.get(`/notification-contacts/${id}/cameras/`)
    return response.data
  },

  linkCameras: async (id: number, camera_ids: number[]): Promise<{ detail: string; linked_camera_ids: number[] }> => {
    const response = await apiClient.post(`/notification-contacts/${id}/cameras/`, { camera_ids })
    return response.data
  },

  unlinkCamera: async (id: number, cameraId: number): Promise<void> => {
    await apiClient.delete(`/notification-contacts/${id}/cameras/${cameraId}/`)
  },

  getCameraAvailableClasses: async (contactId: number, cameraId: number): Promise<{ camera_id: number; available_class_names: string[] }> => {
    const response = await apiClient.get(`/notification-contacts/${contactId}/cameras/${cameraId}/available-classes/`)
    return response.data
  },

  updateCameraClasses: async (contactId: number, cameraId: number, alertClassNames: string[]): Promise<ContactCameraLink> => {
    const response = await apiClient.patch(`/notification-contacts/${contactId}/cameras/${cameraId}/classes/`, { alert_class_names: alertClassNames })
    return response.data
  },
}

// ============================================================================
// Admin User Management API
// ============================================================================

export const adminApi = {
  listUsers: async (): Promise<AdminUser[]> => {
    const response = await apiClient.get('/admin/users/')
    // DefaultRouter wraps in pagination — unwrap if needed
    return Array.isArray(response.data) ? response.data : (response.data.results ?? [])
  },

  createUser: async (data: AdminUserCreateRequest): Promise<AdminUser> => {
    const response = await apiClient.post('/admin/users/', data)
    return response.data
  },

  updateUser: async (id: number, data: AdminUserUpdateRequest): Promise<AdminUser> => {
    const response = await apiClient.patch(`/admin/users/${id}/`, data)
    return response.data
  },

  // #373 — needed when the user owns cameras (the API says so with master_password_required).
  deleteUser: async (id: number, masterPassword?: string): Promise<void> => {
    await apiClient.delete(`/admin/users/${id}/`, masterPassword ? { data: { master_password: masterPassword } } : undefined)
  },

  /**
   * #194 — reset a locked-out user's 2FA. A user who loses their enrolled phone cannot get in:
   * turning 2FA off needs a login, and logging in needs the code. Superuser only.
   */
  clear2fa: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/admin/users/${id}/clear-2fa/`)
    return response.data
  },

  // #076 — SIM mismatch review (superuser-only endpoints)
  listSimMismatches: async (): Promise<SimMismatch[]> => {
    const response = await apiClient.get('/admin/cameras/sim-mismatches/')
    return response.data.results ?? []
  },

  approveReportedImsi: async (cameraId: number, reason?: string): Promise<void> => {
    await apiClient.post(`/admin/cameras/${cameraId}/approve-reported-imsi/`, { reason })
  },

  replaceImsi: async (cameraId: number, newImsi: string, reason?: string): Promise<void> => {
    await apiClient.post(`/admin/cameras/${cameraId}/replace-imsi/`, { new_imsi: newImsi, reason })
  },

  getSimAudit: async (cameraId: number): Promise<SimAudit[]> => {
    const response = await apiClient.get(`/admin/cameras/${cameraId}/sim-audit/`)
    return response.data.results ?? []
  },
}

// ============================================================================
// System Settings API
// ============================================================================

export const systemSettingsApi = {
  // #248 — the tab the Events page opens on, for every user. GET: any user; PATCH: super-admin.
  // Not the per-camera default (cameraGroupsApi.setDefaultEventsView / camera.default_event_category).
  getGlobalDefaultEventsView: async (): Promise<{ category: DefaultEventCategory }> => {
    const response = await apiClient.get('/system-settings/default-events-view/')
    return response.data
  },

  setGlobalDefaultEventsView: async (category: DefaultEventCategory): Promise<{ category: DefaultEventCategory }> => {
    const response = await apiClient.patch('/system-settings/default-events-view/', { category })
    return response.data
  },

  // #287 — the map settings for everyone (GET: any user; PATCH: superuser). The dashboard reads
  // them with the map config; the popup writes them here.
  getMapSettings: async (): Promise<MapSettings> => {
    const response = await apiClient.get('/system-settings/map/')
    return response.data
  },

  setMapSettings: async (change: Partial<MapSettings>): Promise<MapSettings> => {
    const response = await apiClient.patch('/system-settings/map/', change)
    return response.data
  },

  // #294 redesign — the critical sound rules for everyone (GET: any user; PATCH: superuser, only the
  // keys that change; returns the whole object).
  getCriticalSound: async (): Promise<CriticalSoundSettings> => {
    const response = await apiClient.get('/system-settings/critical-sound/')
    return response.data
  },

  updateCriticalSound: async (patch: Partial<CriticalSoundSettings>): Promise<CriticalSoundSettings> => {
    const response = await apiClient.patch('/system-settings/critical-sound/', patch)
    return response.data
  },

  getAlertLinkExpiry: async (): Promise<{ expiry_hours: number | null }> => {
    const response = await apiClient.get('/system-settings/alert-link-expiry/')
    return response.data
  },

  setAlertLinkExpiry: async (expiry_hours: number | null): Promise<{ expiry_hours: number | null }> => {
    const response = await apiClient.patch('/system-settings/alert-link-expiry/', { expiry_hours })
    return response.data
  },

  // #118 — how processed videos/images draw bounding-box labels (super-admin).
  getBoxLabelMode: async (): Promise<{ mode: BoxLabelMode }> => {
    const response = await apiClient.get('/system-settings/box-label-mode/')
    return response.data
  },

  setBoxLabelMode: async (mode: BoxLabelMode): Promise<{ mode: BoxLabelMode }> => {
    const response = await apiClient.patch('/system-settings/box-label-mode/', { mode })
    return response.data
  },

  // #143 — live frame-sampling density used by the task handler (super-admin).
  getFrameSamplingConfig: async (): Promise<FrameSamplingConfig> => {
    const response = await apiClient.get('/system-settings/frame-sampling/')
    return response.data
  },

  setFrameSamplingConfig: async (
    cfg: { target_fps?: number | null; max_detection_frames?: number | null },
  ): Promise<FrameSamplingConfig> => {
    const response = await apiClient.patch('/system-settings/frame-sampling/', cfg)
    return response.data
  },

  // #155 — live event-vote tie-break margin used by the task handler (super-admin).
  getEventVoteTiebreak: async (): Promise<EventVoteTiebreakConfig> => {
    const response = await apiClient.get('/system-settings/event-vote-tiebreak/')
    return response.data
  },

  setEventVoteTiebreak: async (
    cfg: { margin: number | null },
  ): Promise<EventVoteTiebreakConfig> => {
    const response = await apiClient.patch('/system-settings/event-vote-tiebreak/', cfg)
    return response.data
  },
}

export type BoxLabelMode = 'none' | 'name' | 'name_conf'

export interface FrameSamplingConfig {
  target_fps: number
  max_detection_frames: number
}

export interface EventVoteTiebreakConfig {
  /** 0 = off. How close two headlines must be before frame count decides (#155). */
  margin: number
}

// ============================================================================
// Test Cameras API (admin only — camera_test app)
// ============================================================================

// ============================================================================
// Annotations API (admin only)
// ============================================================================

export const annotationsApi = {
  // Projects
  listProjects: async (): Promise<PaginatedResponse<AnnotationProject>> => {
    const response = await apiClient.get('/annotations/projects/')
    return response.data
  },

  getProject: async (id: number): Promise<AnnotationProject> => {
    const response = await apiClient.get(`/annotations/projects/${id}/`)
    return response.data
  },

  createProject: async (data: { name: string; description?: string; class_set: { id: number; name: string; color: string }[] }): Promise<AnnotationProject> => {
    const response = await apiClient.post('/annotations/projects/', data)
    return response.data
  },

  updateProject: async (id: number, data: Partial<{ name: string; description: string; class_set: { id: number; name: string; color: string }[] }>): Promise<AnnotationProject> => {
    const response = await apiClient.patch(`/annotations/projects/${id}/`, data)
    return response.data
  },

  deleteProject: async (id: number): Promise<void> => {
    await apiClient.delete(`/annotations/projects/${id}/`)
  },

  // Import
  importPreview: async (projectId: number, filters: ImportFilters): Promise<ImportPreview> => {
    const response = await apiClient.post(`/annotations/projects/${projectId}/import-preview/`, filters)
    return response.data
  },

  importFromPipeline: async (projectId: number, filters: ImportFilters): Promise<ImportResult> => {
    const response = await apiClient.post(`/annotations/projects/${projectId}/import/`, filters)
    return response.data
  },

  // Available options for import filters
  availableClasses: async (): Promise<{ classes: string[] }> => {
    const response = await apiClient.get('/annotations/projects/available-classes/')
    return response.data
  },

  availableCameras: async (): Promise<{ cameras: { id: number; name: string; imei: string }[] }> => {
    const response = await apiClient.get('/annotations/projects/available-cameras/')
    return response.data
  },

  // Export
  exportYolo: async (projectId: number, filters: {
    classes?: string[]
    cameras?: string[]
    date_from?: string | null
    date_to?: string | null
    status?: string[]
    train_split?: number
    include_all_labels?: boolean
  }): Promise<{ download_url: string }> => {
    const response = await apiClient.post(`/annotations/projects/${projectId}/export/yolo/`, filters)
    return response.data
  },

  // Tasks
  listTasks: async (projectId: number, params?: { status?: string; camera?: string; page?: number }): Promise<PaginatedResponse<AnnotationTask>> => {
    const response = await apiClient.get(`/annotations/projects/${projectId}/tasks/`, { params })
    return response.data
  },

  getTask: async (projectId: number, taskId: number): Promise<AnnotationTaskDetail> => {
    const response = await apiClient.get(`/annotations/projects/${projectId}/tasks/${taskId}/`)
    return response.data
  },

  updateTask: async (projectId: number, taskId: number, data: Partial<{ status: string; assigned_to: number | null }>): Promise<AnnotationTask> => {
    const response = await apiClient.patch(`/annotations/projects/${projectId}/tasks/${taskId}/`, data)
    return response.data
  },

  saveAnnotations: async (projectId: number, taskId: number, data: { annotations: Omit<AnnotationBBox, 'id' | 'created_by' | 'created_at'>[]; status?: string }): Promise<AnnotationTaskDetail> => {
    const response = await apiClient.put(`/annotations/projects/${projectId}/tasks/${taskId}/annotations/`, data)
    return response.data
  },

  getNeighbors: async (projectId: number, taskId: number): Promise<{ prev_id: number | null; next_id: number | null }> => {
    const response = await apiClient.get(`/annotations/projects/${projectId}/tasks/${taskId}/neighbors/`)
    return response.data
  },
}

export const testCamerasApi = {
  list: async (): Promise<TestCamera[]> => {
    const response = await apiClient.get('/test-cameras/')
    return response.data
  },

  detail: async (imei: string): Promise<TestCameraDetail> => {
    const response = await apiClient.get(`/test-cameras/${imei}/`)
    return response.data
  },

  media: async (imei: string): Promise<TestMediaUpload[]> => {
    const response = await apiClient.get(`/test-cameras/${imei}/media/`)
    return response.data
  },
}

// ============================================================================
// Camera Exchange Logs API (#059 — superuser only; IsAdminUser on the backend)
// ============================================================================

// #224 — G100 communication log (superuser only; the API enforces it)
export const g100LogsApi = {
  list: async (params: { imei?: string; channel?: string; ok?: string; page?: number }) => {
    const response = await apiClient.get('/admin/g100-logs/', { params })
    return response.data as PaginatedResponse<G100Log>
  },
}

// #309 — G100 Test Console (stage only). Every endpoint answers 404 where the console is off.
// #403 — the owner API for a G100 camera (items 225/313): what the camera's owner may read and change.
export const g100OwnerApi = {
  status: async (cameraId: number): Promise<G100OwnerStatus> => {
    const response = await apiClient.get(`/cameras/${cameraId}/g100/`)
    return response.data
  },
  changeSettings: async (cameraId: number, changes: Record<string, unknown>): Promise<G100OwnerSettingsChange> => {
    const response = await apiClient.patch(`/cameras/${cameraId}/g100/settings/`, changes)
    return response.data
  },
  // #411 — the action history (the last 50 rows) and queueing one of the six owner actions
  actions: async (cameraId: number): Promise<{ results: G100OwnerAction[] }> => {
    const response = await apiClient.get(`/cameras/${cameraId}/g100/actions/`)
    return response.data
  },
  sendAction: async (cameraId: number, body: G100OwnerActionRequest): Promise<G100OwnerAction> => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/actions/`, body)
    return response.data
  },
}

export const g100ConsoleApi = {
  cameras: async () => {
    const response = await apiClient.get('/g100-console/cameras/')
    return response.data as { results: G100ConsoleCamera[] }
  },
  addDevice: async (body: G100AddDeviceRequest) => {
    const response = await apiClient.post('/g100-console/devices/', body)
    return response.data as G100AddDeviceResult
  },
  info: async (cameraId: number) => {
    const response = await apiClient.get(`/g100-console/cameras/${cameraId}/`)
    return response.data as G100ConsoleInfo
  },
  commands: async (cameraId: number) => {
    const response = await apiClient.get(`/g100-console/cameras/${cameraId}/actions/`)
    return response.data as { results: G100ConsoleCommand[] }
  },
  send: async (cameraId: number, body: G100ConsoleSendRequest) => {
    const response = await apiClient.post(`/g100-console/cameras/${cameraId}/actions/`, body)
    return response.data as G100ConsoleCommand
  },
  wake: async (cameraId: number) => {
    const response = await apiClient.post(`/g100-console/cameras/${cameraId}/wake/`)
    return response.data as G100ConsoleCommand
  },
  messages: async (cameraId: number, params: { page?: number }) => {
    const response = await apiClient.get(`/g100-console/cameras/${cameraId}/messages/`, { params })
    return response.data as PaginatedResponse<G100ConsoleMessage>
  },
  // #379 — the Settings card: every setting of 6.14.4, the advanced three included.
  settings: async (cameraId: number) => {
    const response = await apiClient.get(`/g100-console/cameras/${cameraId}/settings/`)
    return response.data as G100ConsoleSettings
  },
  changeSettings: async (cameraId: number, changes: Record<string, unknown>) => {
    const response = await apiClient.patch(`/g100-console/cameras/${cameraId}/settings/`, changes)
    return response.data as G100ConsoleSettingsChange
  },
  // #383 — the default audio package, one for every camera: load files into it (one audio.tar, or WAV
  // files named by slot), or download it as the camera does. Other slots are kept unless removeOthers.
  loadPackage: async (files: File[], removeOthers: boolean) => {
    const form = new FormData()
    files.forEach((file) => form.append('files', file))
    form.append('remove_others', removeOthers ? 'true' : 'false')
    // As for the library upload: the browser adds the multipart boundary.
    const response = await apiClient.post('/g100-console/audio-package/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data as G100PackageLoadResult
  },
  // Through the API client, so the sign-in header goes with it (a plain link would be refused).
  downloadPackage: async () => {
    const response = await apiClient.get('/g100-console/audio-package/audio.tar', { responseType: 'blob' })
    return response.data as Blob
  },
  // #399 — firmware files and the otaUpdate send (the device downloads the file from our host).
  firmware: async () => {
    const response = await apiClient.get('/g100-console/firmware/')
    return response.data as { results: G100Firmware[] }
  },
  uploadFirmware: async (body: G100FirmwareUpload) => {
    const form = new FormData()
    form.append('file', body.file)
    form.append('channel', String(body.channel))
    form.append('version', body.version)
    form.append('note', body.note)
    // As for the package load: the browser adds the multipart boundary.
    const response = await apiClient.post('/g100-console/firmware/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data as G100Firmware
  },
  deleteFirmware: async (id: number) => {
    await apiClient.delete(`/g100-console/firmware/${id}/`)
  },
  sendFirmware: async (cameraId: number, id: number, confirmName: string) => {
    const response = await apiClient.post(`/g100-console/cameras/${cameraId}/firmware/${id}/send/`,
      { confirm_name: confirmName })
    return response.data as G100ConsoleCommand
  },
}

// #310, #380 — G100 audio: the camera owner's library and the four audio commands, V1.1 6.12.20-22 + V1.4 6.12.23 (owner or
// superuser; a shared user is refused). The commands and their answers appear in the console's
// action history like every other command.
export const g100AudioApi = {
  get: async (cameraId: number) => {
    const response = await apiClient.get(`/cameras/${cameraId}/g100/audio/`)
    return response.data as G100AudioOverview
  },
  upload: async (cameraId: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    // The client's default JSON content type would make axios turn the form into JSON; with this
    // one it sends the form as it is and the browser adds the multipart boundary.
    const response = await apiClient.post(`/cameras/${cameraId}/g100/audio/library/`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data as G100AudioFile
  },
  remove: async (cameraId: number, audioId: number) => {
    await apiClient.delete(`/cameras/${cameraId}/g100/audio/library/${audioId}/`)
  },
  download: async (cameraId: number, body: G100AudioDownloadRequest) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/audio/download/`, body)
    return response.data as G100AudioCommand
  },
  deleteFromCamera: async (cameraId: number, body: G100AudioDeleteRequest) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/audio/delete/`, body)
    return response.data as G100AudioCommand
  },
  setSpecies: async (cameraId: number, body: G100AudioSpeciesRequest) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/audio/species/`, body)
    return response.data as G100AudioCommand
  },
  // #380 — audioPlayback (V1.4 6.12.23): play one sound on the camera, or turn it off.
  playback: async (cameraId: number, body: G100AudioPlaybackRequest) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/audio/playback/`, body)
    return response.data as G100AudioCommand
  },
}

export const cameraExchangeLogsApi = {
  list: async (params: { imei?: string; kind?: string; camera?: number; page?: number }) => {
    const response = await apiClient.get('/admin/camera-exchange-logs/', { params })
    return response.data as PaginatedResponse<CameraExchangeLog>
  },
}

// ============================================================================
// Google Maps Platform (#080)
// ============================================================================

export { mapsApi } from './maps'

// #311 L6 — G100 live view (owner + superusers; the API is the gate). Errors are {code, detail[, reason,
// camera_reason]}. A connection's signed_url carries a viewer-only token: never log it.
export const g100LiveApi = {
  status: async (cameraId: number) => {
    const response = await apiClient.get(`/cameras/${cameraId}/g100/live/`)
    return response.data as G100LiveStatus
  },
  // #381: the console's liveAction form may set maximumSessionLength, idlePeriod and codec; `{}` = the defaults.
  start: async (cameraId: number, body: G100LiveStartRequest = {}) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/live/`, body)
    return response.data as G100LiveStart
  },
  heartbeat: async (cameraId: number, sessionId: number, viewerId: number, connected: boolean) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/live/${sessionId}/heartbeat/`, { viewer: viewerId, connected })
    return response.data as { session: G100LiveSession }
  },
  refresh: async (cameraId: number, sessionId: number, viewerId: number) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/live/${sessionId}/refresh/`, { viewer: viewerId })
    return response.data as { connection: G100LiveConnection }
  },
  leave: async (cameraId: number, sessionId: number, viewerId: number) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/live/${sessionId}/leave/`, { viewer: viewerId })
    return response.data as { session: G100LiveSession }
  },
  stop: async (cameraId: number, sessionId: number) => {
    const response = await apiClient.post(`/cameras/${cameraId}/g100/live/${sessionId}/stop/`, {})
    return response.data as { session: G100LiveSession }
  },
  /** The tab is going away: a keepalive request outlives the page; axios cannot. */
  leaveOnUnload: (cameraId: number, sessionId: number, viewerId: number): void => {
    const token = useAuthStore.getState().accessToken
    void fetch(`${apiClient.defaults.baseURL}/cameras/${cameraId}/g100/live/${sessionId}/leave/`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ viewer: viewerId }),
    }).catch(() => undefined)
  },
}
